import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Prism from 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-sql';
import { useProject } from '../../hooks/useProjects';
import { useMessages } from '../../hooks/useMessages';
import { useWebSocket } from '../../hooks/useWebSocket';
import { PreviewIframe } from './PreviewIframe';
import { projectService } from '../../services/project.service';
import { SandboxStatus, CodeTab } from '../../types/project.types';
import { useToast } from '../../hooks/useToast';
import { Message } from '../../types/message.types';

interface PreviewPanelProps {
  projectId: string;
  /** Called when a sandbox_running WS event fires (build complete). */
  onSandboxRunning?: () => void;
  /** Which code file tab is currently open (null = show live preview). Controlled by parent. */
  activeCodeFile: CodeTab | null;
  /** Parent handler to change the active code file (or null to go back to preview). */
  onActiveCodeFileChange: (file: CodeTab | null) => void;
}

// ─── Language map ──────────────────────────────────────────────────────────

const TAB_LANG: Record<CodeTab, string> = {
  frontend: 'jsx',
  backend: 'javascript',
  database: 'sql',
};

// ─── Helpers ───────────────────────────────────────────────────────────────

function escapeHTML(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function countLines(code: string): number {
  const lines = code.split('\n');
  return lines[lines.length - 1] === '' ? lines.length - 1 : lines.length;
}

// ─── Icons ─────────────────────────────────────────────────────────────────

function ClipboardIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="2" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// ─── Code viewer ───────────────────────────────────────────────────────────

const CODE_STYLE: React.CSSProperties = { fontSize: '12px', lineHeight: 1.6 };

function CodeViewer({ code, lang }: { code: string; lang: string }) {
  const [copied, setCopied] = useState(false);

  const highlighted = useMemo(() => {
    const prismLang = lang === 'jsx' ? 'jsx' : lang;
    const grammar = Prism.languages[prismLang];
    return grammar
      ? Prism.highlight(code, grammar, prismLang)
      : escapeHTML(code);
  }, [code, lang]);

  const lineCount = useMemo(() => countLines(code), [code]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [code]);

  return (
    <div className="flex-1 overflow-auto bg-gray-900 flex relative">
      {/* Copy button */}
      <button
        onClick={handleCopy}
        className="absolute top-3 right-3 z-10 p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-600 transition-colors"
        title={copied ? 'Copied!' : 'Copy code'}
        aria-label={copied ? 'Copied!' : 'Copy code'}
      >
        {copied ? <CheckIcon /> : <ClipboardIcon />}
      </button>

      {/* Line numbers — sticky on horizontal scroll */}
      <div
        className="sticky left-0 z-10 select-none text-right text-gray-500 bg-gray-900 border-r border-gray-700 flex-shrink-0 font-mono"
        style={{ ...CODE_STYLE, padding: '1rem 0.6rem 1rem 0.75rem' }}
      >
        {Array.from({ length: lineCount }, (_, i) => (
          <div key={i}>{i + 1}</div>
        ))}
      </div>

      {/* Prism-highlighted code — rendered untouched, no HTML surgery */}
      <pre
        className={`language-${lang} font-mono flex-1 bg-transparent text-gray-100`}
        style={{ ...CODE_STYLE, margin: 0, padding: '1rem', whiteSpace: 'pre' }}
        dangerouslySetInnerHTML={{ __html: highlighted }}
      />
    </div>
  );
}

// ─── Payload parsing ───────────────────────────────────────────────────────

interface AssistantPayload {
  frontend?: string;
  backend?: string;
  database?: string;
}

function getLatestCode(messages: Message[] | undefined): AssistantPayload | null {
  if (!messages) return null;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'assistant') {
      try {
        let raw = messages[i].content.trim();
        if (raw.startsWith('```')) {
          raw = raw.replace(/^```(?:json)?\n?/, '').replace(/\n?```$/, '').trim();
        }
        const parsed: AssistantPayload = JSON.parse(raw);
        if (parsed.frontend || parsed.backend || parsed.database) return parsed;
      } catch { /* skip */ }
    }
  }
  return null;
}

// ─── Status / build helpers ────────────────────────────────────────────────

// i18n key suffixes under `preview.status.*` — SandboxStatus values are
// already lowerCamel-compatible so the status itself is the key.
const STATUS_DOT: Record<SandboxStatus, string> = {
  none: 'bg-gray-300',
  queued: 'bg-amber-400 animate-pulse',
  building: 'bg-amber-400 animate-pulse',
  running: 'bg-green-500',
  error: 'bg-red-500',
  stopped: 'bg-gray-400',
};

const ACTIVE = new Set<SandboxStatus>(['queued', 'building']);

// Raw WS `step` values → `preview.buildStep.*` i18n key suffixes.
const BUILD_STEP_I18N_KEY: Record<string, string> = {
  writing_files: 'writingFiles',
  backend_build: 'backendBuild',
  frontend_build: 'frontendBuild',
  schema: 'schema',
  nginx: 'nginx',
  auto_repair: 'autoRepair',
};

function summarizeSandboxError(message: string | null): string | null {
  if (!message) return null;
  const lines = message
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const summary = lines.find((line) => !line.startsWith('phase=') && !line.startsWith('project='));
  return summary ?? lines[0] ?? null;
}

// ─── Code tab bar ───────────────────────────────────────────────────────────
// i18n key suffixes under `preview.tabs.*`.
const CODE_TABS: { key: CodeTab; labelKey: string }[] = [
  { key: 'frontend', labelKey: 'frontend' },
  { key: 'backend', labelKey: 'backend' },
  { key: 'database', labelKey: 'database' },
];

// ─── Main component ────────────────────────────────────────────────────────

export function PreviewPanel({
  projectId,
  onSandboxRunning,
  activeCodeFile,
  onActiveCodeFileChange,
}: PreviewPanelProps) {
  const { t } = useTranslation('project');
  const { showToast } = useToast();
  const { data: project, refetch } = useProject(projectId);
  const { data: messages } = useMessages(projectId);
  const [building, setBuilding] = useState(false);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [hasQueuedPreviewUpdate, setHasQueuedPreviewUpdate] = useState(false);

  const [sandboxLastError, setSandboxLastError] = useState<string | null>(null);
  const [buildStep, setBuildStep] = useState<string | null>(null);
  const [buildMessage, setBuildMessage] = useState<string | null>(null);

  const hasFiles = messages?.some((m) => m.role === 'assistant') ?? false;
  const status: SandboxStatus = project?.sandboxStatus ?? 'none';
  const isActive = ACTIVE.has(status) || building;
  const latestCode = getLatestCode(messages);

  // During a rebuild nginx briefly has no route for the preview (old sandbox
  // stopped -> conf rewritten/reloaded); a request landing in that window
  // gets a 404 that would otherwise stick in the iframe until a manual
  // refresh. Bump an epoch on every transition INTO `running` so the iframe
  // remounts with a cache-busted URL and always shows the fresh sandbox.
  const [runningEpoch, setRunningEpoch] = useState(0);
  const prevStatusRef = useRef<SandboxStatus>(status);
  useEffect(() => {
    if (status === 'running' && prevStatusRef.current !== 'running') {
      setRunningEpoch((e) => e + 1);
    }
    prevStatusRef.current = status;
  }, [status]);

  const isActiveRef = useRef(isActive);
  useEffect(() => { isActiveRef.current = isActive; }, [isActive]);

  const onSandboxRunningRef = useRef(onSandboxRunning);
  useEffect(() => { onSandboxRunningRef.current = onSandboxRunning; }, [onSandboxRunning]);

  useEffect(() => {
    if (status !== 'error') return;
    projectService.getSandboxState(projectId).then((s) => {
      setSandboxLastError(s.last_error ?? null);
    }).catch(() => {});
  }, [projectId, status]);

  const handleMessage = useCallback(
    (type: string, data: unknown) => {
      if (type === 'preview_update' && isActiveRef.current) {
        setHasQueuedPreviewUpdate(true);
      }
      if (type === 'sandbox_building') {
        const payload = data as { step?: string; message?: string } | undefined;
        setBuildStep(payload?.step ?? null);
        setBuildMessage(payload?.message ?? null);
      }
      if (type === 'sandbox_running' || type === 'sandbox_error' || type === 'sandbox_stopped') {
        refetch();
        setBuilding(false);
        setHasQueuedPreviewUpdate(false);
      }
      if (type === 'sandbox_running') {
        setSandboxLastError(null);
        setBuildStep(null);
        setBuildMessage(null);
        onSandboxRunningRef.current?.();
      }
      if (type === 'sandbox_error') {
        const msg = (data as { error?: string })?.error ?? 'Build failed';
        setBuildError(msg);
        setBuildStep(null);
        showToast('Preview build failed. Check your prompt and try again.', 'error');
        projectService.getSandboxState(projectId).then((s) => {
          setSandboxLastError(s.last_error ?? null);
        }).catch(() => {});
      }
      if (type === 'sandbox_stopped') {
        setBuildStep(null);
        setBuildMessage(null);
      }
    },
    [projectId, refetch, showToast]
  );

  useWebSocket({ projectId, onMessage: handleMessage });

  async function handleBuild() {
    setBuildError(null);
    setSandboxLastError(null);
    setBuildStep('writing_files');
    setBuildMessage('Preparing build...');
    setBuilding(true);
    try {
      await projectService.triggerBuild(projectId);
      refetch();
    } catch {
      setBuilding(false);
      setBuildError('Could not start build — try again.');
    }
  }

  async function handleDownload() {
    setDownloadError(null);
    setDownloading(true);
    try {
      const response = await fetch(`/api/projects/${projectId}/download`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error((data as { message?: string }).message ?? 'Download failed');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project?.name ?? projectId}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Download failed');
    } finally {
      setDownloading(false);
    }
  }

  const statusLabel = t(`preview.status.${status}`);
  const buildStepLabel = buildStep
    ? t(`preview.buildStep.${BUILD_STEP_I18N_KEY[buildStep] ?? buildStep}`, { defaultValue: statusLabel })
    : statusLabel;
  const effectiveBuildLabel = isActive ? buildStepLabel : statusLabel;
  const buttonLabel = status === 'running' ? t('preview.buttonRebuild') : t('preview.buttonBuild');
  const visibleError = summarizeSandboxError(sandboxLastError) ?? buildError;

  return (
    <div className="flex flex-col h-full">
      {/* ── Toolbar: status, tabs, actions — one row, scrolls as a whole
             on widths too narrow to fit everything (small phones) ──── */}
      <div className="border-b border-gray-200 bg-white shrink-0 flex items-center gap-3 px-4 py-2 overflow-x-auto">
        <div className="flex items-center gap-1.5 shrink-0" title={effectiveBuildLabel}>
          <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[status]}`} aria-hidden="true" />
          <span className="text-xs text-gray-600 whitespace-nowrap">{effectiveBuildLabel}</span>
        </div>

        <div className="w-px h-4 bg-gray-200 shrink-0" aria-hidden="true" />

        <div className="flex gap-0.5 shrink-0">
          <button
            onClick={() => onActiveCodeFileChange(null)}
            className={`text-xs px-2.5 py-1 rounded font-medium transition-colors whitespace-nowrap ${
              activeCodeFile === null
                ? 'bg-orange-50 text-orange-600'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            {t('preview.tabs.preview')}
          </button>
          {CODE_TABS.filter(({ key }) => !!latestCode?.[key]).map(({ key, labelKey }) => (
            <button
              key={key}
              onClick={() => onActiveCodeFileChange(key)}
              className={`text-xs px-2.5 py-1 rounded font-medium transition-colors whitespace-nowrap ${
                activeCodeFile === key
                  ? 'bg-orange-50 text-orange-600'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t(`preview.tabs.${labelKey}`)}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 shrink-0 ml-auto">
          {project?.previewUrl && status === 'running' && (
            <a
              href={project.previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-700 whitespace-nowrap"
            >
              {t('preview.open')}
            </a>
          )}
          {hasFiles && (
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="text-sm px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
            >
              {downloading ? t('preview.downloading') : t('preview.download')}
            </button>
          )}
          <button
            onClick={handleBuild}
            disabled={isActive}
            className="text-sm px-3 py-1.5 rounded bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
          >
            {isActive ? effectiveBuildLabel : buttonLabel}
          </button>
        </div>
      </div>

      {/* ── Banners — only shown for transient/actionable states ──────── */}
      {isActive && buildStep === 'auto_repair' && (
        <div className="border-b border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800 shrink-0">
          {t('preview.autoRepairNotice')}
        </div>
      )}
      {isActive && buildMessage && (
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-1.5 text-xs text-gray-500 shrink-0">
          {buildMessage}
        </div>
      )}
      {hasQueuedPreviewUpdate && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800 shrink-0">
          {t('preview.newCodeReady')}
        </div>
      )}
      {(buildError || (status === 'error' && sandboxLastError)) && (
        <div className="px-4 py-3 bg-red-50 border-b border-red-200 shrink-0">
          <p className="text-sm text-red-700 mb-1">{t('preview.buildFailed')}</p>
          {sandboxLastError && (
            <p className="font-mono text-xs text-red-600 line-clamp-3 break-all">
              {visibleError}
            </p>
          )}
          {!sandboxLastError && buildError && (
            <p className="text-xs text-red-600">{buildError}</p>
          )}
          <button
            onClick={handleBuild}
            disabled={isActive}
            className="mt-2 text-xs px-2 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50 transition-colors"
          >
            {t('preview.tryRebuilding')}
          </button>
        </div>
      )}
      {downloadError && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-sm text-red-700 shrink-0">
          {downloadError}
        </div>
      )}

      {/* ── Content area ────────────────────────────────────────────── */}
      {activeCodeFile !== null ? (
        <CodeViewer
          code={latestCode?.[activeCodeFile] ?? ''}
          lang={TAB_LANG[activeCodeFile]}
        />
      ) : (
        <PreviewIframe
          previewUrl={status === 'running' ? project?.previewUrl : undefined}
          hasFiles={hasFiles}
          isBuilding={isActive}
          reloadEpoch={runningEpoch}
        />
      )}
    </div>
  );
}
